import type { TimePanelHeaderBarProps } from './components/TimePanelHeaderBar';
import type { TimePanelProps } from './components/TimePanel';
import type { ReactNode, RefAttributes } from 'react';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- Just importing for types
import type * as index from './index';
import {
    createLazyComponent,
    LazyAction,
    type LazyComponent,
    LazyImport,
    LazyModule,
} from 'owa-bundling';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { lazyInitializeTimePanelData } from './lazyTimePanelData';

const lazyModule: LazyModule<typeof index> = new LazyModule(
    () => import(/* webpackChunkName: "CalendarBootTimePanel"*/ './index'),
    { name: 'CalendarBootTimePanel' }
);

const fullTimePanelInitialization: LazyImport<
    typeof index.lazyInitializeTimePanel.importAndExecute,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.lazyInitializeTimePanel.importAndExecute);

// Decide before importing anything, so the deferred path loads only the TimePanelData chunk.
const initializationLazyModule: LazyModule<{
    initializeTimePanel: typeof index.lazyInitializeTimePanel.importAndExecute;
}> = new LazyModule(
    async () => ({
        initializeTimePanel:
            isGlobalFeatureEnabled(
                'perf-memory-defer-idle-ui',
                true /* dontThrowErrorIfNotInitialized */
            ) &&
            !isGlobalFeatureEnabled(
                'perf-memory-defer-time-panel-ui-ripcord',
                true /* dontThrowErrorIfNotInitialized */
            )
                ? await lazyInitializeTimePanelData.import()
                : await fullTimePanelInitialization.import(),
    }),
    { name: 'TimePanelInitialization' }
);

export const lazyInitializeTimePanelProxy: LazyAction<
    typeof index.lazyInitializeTimePanel.importAndExecute,
    typeof initializationLazyModule
> = new LazyAction(initializationLazyModule, m => m.initializeTimePanel);

export const lazyOpenTasksViewProxy: LazyAction<
    typeof index.lazyOpenTasksView.importAndExecute,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.lazyOpenTasksView.importAndExecute);

export const lazyOpenTimePanelInCalendarViewProxy: LazyAction<
    typeof index.lazyOpenTimePanelInCalendarView.importAndExecute,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.lazyOpenTimePanelInCalendarView.importAndExecute);

export const lazyOpenConflictsViewProxy: LazyAction<
    typeof index.lazyOpenConflictsView.importAndExecute,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.lazyOpenConflictsView.importAndExecute);

export const lazyCloseConflictsViewProxy: LazyAction<
    typeof index.lazyCloseConflictsView.importAndExecute,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.lazyCloseConflictsView.importAndExecute);

export const lazyOpenEventDetailsViewProxy: LazyAction<
    typeof index.lazyOpenEventDetailsView.importAndExecute,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.lazyOpenEventDetailsView.importAndExecute);

export const TimePanel: LazyComponent<
    TimePanelProps & {
        children?: ReactNode | undefined;
    } & RefAttributes<unknown>,
    unknown,
    void
> = createLazyComponent(lazyModule, m => m.TimePanel);
export const TimePanelHeaderBar: LazyComponent<
    TimePanelHeaderBarProps & {
        children?: ReactNode | undefined;
    } & RefAttributes<unknown>,
    unknown,
    void
> = createLazyComponent(lazyModule, m => m.TimePanelHeaderBar);
